import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const COUNTRY_OPTIONS = ['UA', 'KZ', 'PL'];
const CURRENCY_OPTIONS = ['UAH', 'KZT', 'EUR'];
const CURRENCY_MAP = { UA: 'UAH', KZ: 'KZT', PL: 'EUR' };

const EMPTY_FORM = {
  country: 'UA',
  name: '',
  minutes: '',
  price_local: '',
  currency: 'UAH',
  price_per_minute_local: '',
  overage_rate_local: '',
  is_active: true,
};

const VoicePackageForm = ({ open, onClose, onSave, entry }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(entry?.id);

  useEffect(() => {
    if (entry) {
      setForm({
        country: entry.country || 'UA',
        name: entry.name || '',
        minutes: entry.minutes ?? '',
        price_local: entry.price_local ?? '',
        currency: entry.currency || 'UAH',
        price_per_minute_local: entry.price_per_minute_local ?? '',
        overage_rate_local: entry.overage_rate_local ?? '',
        is_active: entry.is_active ?? true,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [entry, open]);

  // Auto-set currency when country changes
  useEffect(() => {
    if (form.country && CURRENCY_MAP[form.country]) {
      setForm((prev) => ({ ...prev, currency: CURRENCY_MAP[prev.country] }));
    }
  }, [form.country]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        minutes: form.minutes !== '' ? Number(form.minutes) : 0,
        price_local: form.price_local !== '' ? Number(form.price_local) : 0,
        price_per_minute_local: form.price_per_minute_local !== '' ? Number(form.price_per_minute_local) : 0,
        overage_rate_local: form.overage_rate_local !== '' ? Number(form.overage_rate_local) : 0,
      };
      await onSave(payload, entry?.id);
      onClose();
    } catch {
      // error handled by parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isEdit
          ? t('voicePackages.editPackage', 'Edit Voice Package')
          : t('voicePackages.addPackage', 'Add Voice Package')}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Row 1: Country + Name */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('voicePackages.country', 'Country')}</InputLabel>
              <Select
                value={form.country}
                onChange={handleChange('country')}
                label={t('voicePackages.country', 'Country')}
              >
                {COUNTRY_OPTIONS.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('voicePackages.name', 'Name')}
              value={form.name}
              onChange={handleChange('name')}
              size="small"
              fullWidth
            />
          </Box>

          {/* Row 2: Minutes + Currency */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('voicePackages.minutes', 'Minutes')}
              value={form.minutes}
              onChange={handleChange('minutes')}
              size="small"
              type="number"
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>{t('voicePackages.currency', 'Currency')}</InputLabel>
              <Select
                value={form.currency}
                onChange={handleChange('currency')}
                label={t('voicePackages.currency', 'Currency')}
              >
                {CURRENCY_OPTIONS.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Row 3: Price Local + Price Per Minute Local */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('voicePackages.priceLocal', 'Price Local')}
              value={form.price_local}
              onChange={handleChange('price_local')}
              size="small"
              type="number"
              fullWidth
            />
            <TextField
              label={t('voicePackages.pricePerMinuteLocal', 'Price Per Minute Local')}
              value={form.price_per_minute_local}
              onChange={handleChange('price_per_minute_local')}
              size="small"
              type="number"
              fullWidth
            />
          </Box>

          {/* Row 4: Overage Rate Local */}
          <TextField
            label={t('voicePackages.overageRateLocal', 'Overage Rate Local')}
            value={form.overage_rate_local}
            onChange={handleChange('overage_rate_local')}
            size="small"
            type="number"
            fullWidth
          />

          {/* Row 5: Is Active */}
          <FormControlLabel
            control={
              <Checkbox
                checked={form.is_active}
                onChange={handleChange('is_active')}
                sx={{ '&.Mui-checked': { color: '#00C853' } }}
              />
            }
            label={t('voicePackages.isActive', 'Active')}
          />

          {/* Info text */}
          <Typography variant="caption" color="text.secondary">
            {t(
              'voicePackages.usdInfo',
              'USD prices are updated automatically by the daily cron job'
            )}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !form.name}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: '#00C853',
            '&:hover': { bgcolor: '#00A843' },
          }}
        >
          {saving ? (
            <CircularProgress size={20} sx={{ color: 'white' }} />
          ) : isEdit ? (
            t('common.save', 'Save')
          ) : (
            t('common.create', 'Create')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoicePackageForm;
